import { useEffect, useState } from "react";
import { Wallet, TrendingUp, TrendingDown, Plus, DollarSign, Check, Pencil, Trash2, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import PageHeader from "@/components/PageHeader";
import EmptyState from "@/components/EmptyState";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

function brl(v) {
  return new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(v || 0);
}
function currentComp() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}
function compLabel(comp) {
  if (!comp) return "";
  const [y, m] = comp.split("-");
  const meses = ["Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho", "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro"];
  return `${meses[parseInt(m, 10) - 1]} / ${y}`;
}

const STATUS_TONE = {
  pendente: "bg-amber-100 text-amber-700",
  pago: "bg-emerald-100 text-emerald-700",
  atrasado: "bg-rose-100 text-rose-700",
  cancelado: "bg-slate-100 text-slate-500",
};

export default function Financeiro() {
  const { user } = useAuth();
  const canGerar = user.role === "admin" || user.role === "financeiro";
  const [comp, setComp] = useState(currentComp());
  const [tab, setTab] = useState("mensalidades");
  const [resumo, setResumo] = useState(null);
  const [mensalidades, setMensalidades] = useState([]);
  const [salarios, setSalarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogBaixa, setDialogBaixa] = useState(null);
  const [dialogEdit, setDialogEdit] = useState(null);       // { tipo, item }
  const [confirmDel, setConfirmDel] = useState(null);       // { tipo, item }
  const [confirmEstorno, setConfirmEstorno] = useState(null); // { tipo, item }

  const canAdmin = user.role === "admin" || user.role === "financeiro";

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [m, r, s] = await Promise.all([
        api.get("/financeiro/mensalidades", { params: { competencia: comp } }),
        canGerar ? api.get("/financeiro/resumo", { params: { competencia: comp } }) : Promise.resolve({ data: null }),
        canGerar ? api.get("/financeiro/salarios", { params: { competencia: comp } }) : Promise.resolve({ data: [] }),
      ]);
      setMensalidades(m.data);
      setResumo(r.data);
      setSalarios(s.data);
    } catch (e) {
      toast.error("Erro ao carregar financeiro");
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchAll(); /* eslint-disable-next-line */ }, [comp]);

  const gerarMens = async () => {
    try {
      const { data } = await api.post(`/financeiro/mensalidades/gerar?competencia=${comp}`);
      toast.success(`${data.criadas} mensalidades criadas · ${data.ja_existiam} já existiam`);
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };
  const gerarSal = async () => {
    try {
      const { data } = await api.post(`/financeiro/salarios/gerar?competencia=${comp}`);
      toast.success(`${data.criadas} salários criados · ${data.ja_existiam} já existiam`);
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };

  const doDelete = async () => {
    if (!confirmDel) return;
    try {
      const path = confirmDel.tipo === "mens" ? "mensalidades" : "salarios";
      await api.delete(`/financeiro/${path}/${confirmDel.item.id}`);
      toast.success("Registro excluído");
      setConfirmDel(null);
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao excluir"); }
  };

  const doEstorno = async () => {
    if (!confirmEstorno) return;
    try {
      const path = confirmEstorno.tipo === "mens" ? "mensalidades" : "salarios";
      await api.post(`/financeiro/${path}/${confirmEstorno.item.id}/estornar`);
      toast.success("Pagamento estornado — voltou para pendente");
      setConfirmEstorno(null);
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao estornar"); }
  };

  const compOptions = (() => {
    const arr = [];
    const now = new Date();
    for (let i = -6; i <= 6; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() + i, 1);
      arr.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`);
    }
    return arr;
  })();

  return (
    <div data-testid="financeiro-page">
      <PageHeader
        icon={Wallet}
        title="Controle Financeiro"
        description="Mensalidades dos praticantes e salários dos terapeutas."
        actions={
          <Select value={comp} onValueChange={setComp}>
            <SelectTrigger className="w-52" data-testid="fin-competencia"><SelectValue /></SelectTrigger>
            <SelectContent>{compOptions.map((c) => <SelectItem key={c} value={c}>{compLabel(c)}</SelectItem>)}</SelectContent>
          </Select>
        }
      />

      {resumo && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <ResumoCard tone="emerald" label="Receita prevista" value={brl(resumo.receita_prevista)} icon={TrendingUp} testId="resumo-prevista" />
          <ResumoCard tone="turquoise" label="Recebido" value={brl(resumo.receita_recebida)} icon={Check} testId="resumo-recebido" />
          <ResumoCard tone="amber" label="Pendente" value={brl(resumo.receita_pendente)} icon={DollarSign} testId="resumo-pendente" />
          <ResumoCard tone="rose" label="Salários (mês)" value={brl(resumo.despesa_salarios)} icon={TrendingDown} testId="resumo-salarios" />
        </div>
      )}

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="mensalidades" data-testid="tab-mensalidades">Mensalidades ({mensalidades.length})</TabsTrigger>
          {canGerar && <TabsTrigger value="salarios" data-testid="tab-salarios">Salários ({salarios.length})</TabsTrigger>}
        </TabsList>

        <TabsContent value="mensalidades" className="mt-4">
          <div className="mb-4 flex justify-end">
            {canGerar && <Button onClick={gerarMens} className="bg-brand-turquoise hover:bg-[#3A9CA2]" data-testid="fin-gerar-mens"><Plus className="h-4 w-4 mr-1" /> Gerar mensalidades do mês</Button>}
          </div>
          <Card className="border-border shadow-none rounded-xl bg-white">
            <CardContent className="p-0">
              {loading ? <div className="p-10 text-center text-muted-foreground">Carregando…</div>
                : mensalidades.length === 0 ? <div className="p-6"><EmptyState icon={Wallet} title="Nenhuma mensalidade" description="Clique em 'Gerar mensalidades' para criar as cobranças do mês para todos os praticantes ativos." /></div>
                : (
                  <Table>
                    <TableHeader><TableRow><TableHead>Praticante</TableHead><TableHead>Vencimento</TableHead><TableHead>Valor</TableHead><TableHead>Status</TableHead><TableHead>Pago em</TableHead><TableHead className="w-56 text-right">Ações</TableHead></TableRow></TableHeader>
                    <TableBody>
                      {mensalidades.map((m) => (
                        <TableRow key={m.id} data-testid={`mens-row-${m.id}`}>
                          <TableCell className="font-medium">{m.praticante_nome || m.praticante_nome_snapshot}</TableCell>
                          <TableCell>{m.vencimento}</TableCell>
                          <TableCell>{brl(m.valor)}</TableCell>
                          <TableCell><span className={`text-xs px-2 py-1 rounded-full ${STATUS_TONE[m.status]}`}>{m.status}</span></TableCell>
                          <TableCell className="text-muted-foreground">{m.pago_em || "—"}</TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1 justify-end">
                              {m.status !== "pago" && (
                                <Button size="sm" variant="outline" onClick={() => setDialogBaixa({ tipo: "mens", item: m })} data-testid={`mens-baixa-${m.id}`}>
                                  <Check className="h-3 w-3 mr-1" /> Dar baixa
                                </Button>
                              )}
                              {m.status === "pago" && canAdmin && (
                                <Button size="sm" variant="outline" onClick={() => setConfirmEstorno({ tipo: "mens", item: m })} data-testid={`mens-estorno-${m.id}`} title="Estornar pagamento">
                                  <RotateCcw className="h-3 w-3" />
                                </Button>
                              )}
                              {canAdmin && (
                                <Button size="sm" variant="outline" onClick={() => setDialogEdit({ tipo: "mens", item: m })} data-testid={`mens-edit-${m.id}`} title="Editar">
                                  <Pencil className="h-3 w-3" />
                                </Button>
                              )}
                              {canAdmin && (
                                <Button size="sm" variant="outline" onClick={() => setConfirmDel({ tipo: "mens", item: m })} data-testid={`mens-del-${m.id}`} title="Excluir" className="text-rose-600 hover:text-rose-700 hover:bg-rose-50">
                                  <Trash2 className="h-3 w-3" />
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
            </CardContent>
          </Card>
        </TabsContent>

        {canGerar && (
          <TabsContent value="salarios" className="mt-4">
            <div className="mb-4 flex justify-end">
              <Button onClick={gerarSal} className="bg-brand-turquoise hover:bg-[#3A9CA2]" data-testid="fin-gerar-sal"><Plus className="h-4 w-4 mr-1" /> Gerar salários do mês</Button>
            </div>
            <Card className="border-border shadow-none rounded-xl bg-white">
              <CardContent className="p-0">
                {salarios.length === 0 ? <div className="p-6"><EmptyState icon={Wallet} title="Nenhum salário" description="Clique em 'Gerar salários' para criar as despesas do mês." /></div>
                  : (
                    <Table>
                      <TableHeader><TableRow><TableHead>Terapeuta</TableHead><TableHead>Vencimento</TableHead><TableHead>Valor</TableHead><TableHead>Status</TableHead><TableHead>Pago em</TableHead><TableHead className="w-56 text-right">Ações</TableHead></TableRow></TableHeader>
                      <TableBody>
                        {salarios.map((s) => (
                          <TableRow key={s.id} data-testid={`sal-row-${s.id}`}>
                            <TableCell className="font-medium">{s.terapeuta_nome || s.terapeuta_nome_snapshot}</TableCell>
                            <TableCell>{s.vencimento}</TableCell>
                            <TableCell>{brl(s.valor)}</TableCell>
                            <TableCell><span className={`text-xs px-2 py-1 rounded-full ${STATUS_TONE[s.status]}`}>{s.status}</span></TableCell>
                            <TableCell className="text-muted-foreground">{s.pago_em || "—"}</TableCell>
                            <TableCell>
                              <div className="flex items-center gap-1 justify-end">
                                {s.status !== "pago" && (
                                  <Button size="sm" variant="outline" onClick={() => setDialogBaixa({ tipo: "sal", item: s })}>
                                    <Check className="h-3 w-3 mr-1" /> Dar baixa
                                  </Button>
                                )}
                                {s.status === "pago" && canAdmin && (
                                  <Button size="sm" variant="outline" onClick={() => setConfirmEstorno({ tipo: "sal", item: s })} title="Estornar pagamento">
                                    <RotateCcw className="h-3 w-3" />
                                  </Button>
                                )}
                                {canAdmin && (
                                  <Button size="sm" variant="outline" onClick={() => setDialogEdit({ tipo: "sal", item: s })} title="Editar">
                                    <Pencil className="h-3 w-3" />
                                  </Button>
                                )}
                                {canAdmin && (
                                  <Button size="sm" variant="outline" onClick={() => setConfirmDel({ tipo: "sal", item: s })} title="Excluir" className="text-rose-600 hover:text-rose-700 hover:bg-rose-50">
                                    <Trash2 className="h-3 w-3" />
                                  </Button>
                                )}
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
              </CardContent>
            </Card>
          </TabsContent>
        )}
      </Tabs>

      {dialogBaixa && (
        <BaixaDialog
          data={dialogBaixa}
          onClose={() => setDialogBaixa(null)}
          onDone={() => { setDialogBaixa(null); fetchAll(); }}
        />
      )}

      {dialogEdit && (
        <EditDialog
          data={dialogEdit}
          onClose={() => setDialogEdit(null)}
          onDone={() => { setDialogEdit(null); fetchAll(); }}
        />
      )}

      <AlertDialog open={!!confirmDel} onOpenChange={(o) => !o && setConfirmDel(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir {confirmDel?.tipo === "mens" ? "mensalidade" : "salário"}?</AlertDialogTitle>
            <AlertDialogDescription>
              O registro vai para a Lixeira. Um administrador pode restaurar depois em <b>Administração → Lixeira</b>.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="bg-rose-600 hover:bg-rose-700" data-testid="fin-del-confirm">Excluir</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!confirmEstorno} onOpenChange={(o) => !o && setConfirmEstorno(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Estornar pagamento?</AlertDialogTitle>
            <AlertDialogDescription>
              O status volta para <b>pendente</b> e todos os dados de baixa (valor pago, forma, data) são limpos.
              A ação fica registrada na Auditoria.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={doEstorno} className="bg-brand-lilac hover:bg-[#9670B0]" data-testid="fin-estorno-confirm">Estornar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function ResumoCard({ tone, label, value, icon: Icon, testId }) {
  const TONE = {
    emerald: "bg-emerald-50 text-emerald-700",
    turquoise: "bg-[#DFF4F5] text-[#3A9CA2]",
    amber: "bg-amber-50 text-amber-700",
    rose: "bg-rose-50 text-rose-700",
  };
  return (
    <Card className="border-border shadow-none rounded-xl bg-white" data-testid={testId}>
      <CardContent className="p-5">
        <div className={`h-10 w-10 rounded-lg flex items-center justify-center ${TONE[tone]}`}><Icon className="h-5 w-5" /></div>
        <p className="mt-4 text-2xl font-heading font-bold text-slate-900">{value}</p>
        <p className="mt-1 text-sm text-muted-foreground">{label}</p>
      </CardContent>
    </Card>
  );
}

function BaixaDialog({ data, onClose, onDone }) {
  const { tipo, item } = data;
  const valorOriginal = parseFloat(item.valor) || 0;
  const [form, setForm] = useState({
    pago_em: new Date().toISOString().slice(0, 10),
    forma_pagamento: "pix",
    valor_pago: valorOriginal,
    motivo_ajuste: "",
    observacoes: "",
  });
  const [saving, setSaving] = useState(false);

  const valorPago = parseFloat(form.valor_pago) || 0;
  const diff = valorPago - valorOriginal;
  const hasDiff = Math.abs(diff) >= 0.01;
  const percentDiff = valorOriginal > 0 ? (diff / valorOriginal) * 100 : 0;

  const applyPercent = (pct) => {
    // pct positive = desconto (subtract), pct negative = acréscimo
    const novo = valorOriginal * (1 - pct / 100);
    setForm({ ...form, valor_pago: novo.toFixed(2) });
  };

  const doBaixa = async () => {
    if (hasDiff && !form.motivo_ajuste.trim()) {
      toast.error("Informe o motivo do ajuste no valor");
      return;
    }
    setSaving(true);
    try {
      const endpoint = tipo === "mens" ? `/financeiro/mensalidades/${item.id}/baixar` : `/financeiro/salarios/${item.id}/baixar`;
      const obs = [form.motivo_ajuste, form.observacoes].filter(Boolean).join(" · ");
      await api.post(endpoint, {
        pago_em: form.pago_em,
        forma_pagamento: form.forma_pagamento,
        valor_pago: valorPago || valorOriginal,
        observacoes: obs || undefined,
      });
      toast.success("Baixa realizada");
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="baixa-dialog">
        <DialogHeader>
          <DialogTitle>Dar baixa</DialogTitle>
          <DialogDescription>
            {tipo === "mens" ? "Mensalidade" : "Salário"}
            {item.praticante_nome && ` · ${item.praticante_nome}`}
            {item.terapeuta_nome && ` · ${item.terapeuta_nome}`}
          </DialogDescription>
        </DialogHeader>

        {/* Value summary */}
        <div className="rounded-xl border-2 border-brand-turquoise/30 bg-[#F0FBFC] p-4 space-y-2">
          <div className="flex items-center justify-between text-sm">
            <span className="text-slate-600">Valor original</span>
            <span className="font-semibold text-slate-800">{brl(valorOriginal)}</span>
          </div>
          {hasDiff && (
            <div className={`flex items-center justify-between text-xs ${diff < 0 ? "text-emerald-700" : "text-rose-700"}`}>
              <span>{diff < 0 ? "Desconto" : "Acréscimo"} ({Math.abs(percentDiff).toFixed(1)}%)</span>
              <span className="font-semibold">{diff < 0 ? "−" : "+"}{brl(Math.abs(diff))}</span>
            </div>
          )}
          <div className="flex items-center justify-between text-base font-heading pt-1 border-t border-brand-turquoise/30">
            <span className="text-slate-700 font-semibold">Valor a receber</span>
            <span className="font-bold text-brand-turquoise text-lg">{brl(valorPago)}</span>
          </div>
        </div>

        <div className="space-y-3">
          <F label="Valor pago (edite para aplicar desconto/acréscimo)" req>
            <div className="flex items-center gap-2">
              <div className="relative flex-1">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs text-slate-500">R$</span>
                <Input
                  type="number" step="0.01" min="0"
                  value={form.valor_pago}
                  onChange={(e) => setForm({ ...form, valor_pago: e.target.value })}
                  className="pl-9 font-mono text-base font-semibold"
                  data-testid="baixa-valor-pago"
                />
              </div>
              <Button type="button" size="sm" variant="outline" onClick={() => setForm({ ...form, valor_pago: valorOriginal, motivo_ajuste: "" })}>Reset</Button>
            </div>
            <div className="flex flex-wrap gap-1 mt-2">
              <QuickBtn label="−5%" onClick={() => applyPercent(5)} />
              <QuickBtn label="−10%" onClick={() => applyPercent(10)} />
              <QuickBtn label="−15%" onClick={() => applyPercent(15)} />
              <QuickBtn label="−20%" onClick={() => applyPercent(20)} />
              <QuickBtn label="+5%" onClick={() => applyPercent(-5)} />
              <QuickBtn label="+10%" onClick={() => applyPercent(-10)} />
            </div>
          </F>

          {hasDiff && (
            <F label="Motivo do ajuste" req>
              <Select value={form.motivo_ajuste} onValueChange={(v) => setForm({ ...form, motivo_ajuste: v })}>
                <SelectTrigger data-testid="baixa-motivo"><SelectValue placeholder="Selecione…" /></SelectTrigger>
                <SelectContent>
                  {diff < 0 ? (
                    <>
                      <SelectItem value="Desconto para pagamento antecipado">Desconto — pagamento antecipado</SelectItem>
                      <SelectItem value="Desconto para irmãos">Desconto — irmãos matriculados</SelectItem>
                      <SelectItem value="Desconto por acordo">Desconto — acordo/situação financeira</SelectItem>
                      <SelectItem value="Bolsa parcial">Bolsa parcial</SelectItem>
                      <SelectItem value="Faltas descontadas">Faltas descontadas</SelectItem>
                      <SelectItem value="Cortesia">Cortesia</SelectItem>
                      <SelectItem value="Ajuste manual">Outro (descrever nas observações)</SelectItem>
                    </>
                  ) : (
                    <>
                      <SelectItem value="Juros por atraso">Juros por atraso</SelectItem>
                      <SelectItem value="Multa">Multa</SelectItem>
                      <SelectItem value="Sessão extra">Sessão extra</SelectItem>
                      <SelectItem value="Taxa adicional">Taxa adicional</SelectItem>
                      <SelectItem value="Ajuste manual">Outro (descrever nas observações)</SelectItem>
                    </>
                  )}
                </SelectContent>
              </Select>
            </F>
          )}

          <div className="grid grid-cols-2 gap-3">
            <F label="Data do pagamento" req><Input type="date" value={form.pago_em} onChange={(e) => setForm({ ...form, pago_em: e.target.value })} data-testid="baixa-pago-em" /></F>
            <F label="Forma">
              <Select value={form.forma_pagamento} onValueChange={(v) => setForm({ ...form, forma_pagamento: v })}>
                <SelectTrigger data-testid="baixa-forma"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="pix">PIX</SelectItem>
                  <SelectItem value="dinheiro">Dinheiro</SelectItem>
                  <SelectItem value="credito">Cartão de crédito</SelectItem>
                  <SelectItem value="debito">Cartão de débito</SelectItem>
                  <SelectItem value="transferencia">Transferência</SelectItem>
                  <SelectItem value="boleto">Boleto</SelectItem>
                  <SelectItem value="outro">Outro</SelectItem>
                </SelectContent>
              </Select>
            </F>
          </div>

          <F label="Observações"><Textarea rows={2} value={form.observacoes} onChange={(e) => setForm({ ...form, observacoes: e.target.value })} placeholder="Ex: pagou via chave PIX pessoal, comprovante nº 12345…" /></F>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={doBaixa} disabled={saving || valorPago <= 0} className="bg-brand-turquoise hover:bg-[#3A9CA2] text-white" data-testid="baixa-submit">
            {saving ? "Registrando…" : `Confirmar ${brl(valorPago)}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function QuickBtn({ label, onClick }) {
  return (
    <button type="button" onClick={onClick} className="px-2 py-1 rounded-md border border-border text-[11px] font-medium text-slate-600 hover:bg-slate-100 hover:text-brand-turquoise transition-colors">
      {label}
    </button>
  );
}

function F({ label, req, children }) {
  return (<div className="space-y-1.5"><Label className="text-xs uppercase tracking-wide text-muted-foreground">{label}{req && <span className="text-destructive"> *</span>}</Label>{children}</div>);
}


function EditDialog({ data, onClose, onDone }) {
  const { tipo, item } = data;
  const isMens = tipo === "mens";
  const label = isMens ? "mensalidade" : "salário";
  const nome = isMens
    ? (item.praticante_nome || item.praticante_nome_snapshot)
    : (item.terapeuta_nome || item.terapeuta_nome_snapshot);

  const [form, setForm] = useState({
    valor: item.valor ?? 0,
    vencimento: item.vencimento || "",
    status: item.status || "pendente",
    observacoes: item.observacoes || "",
  });
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    setSaving(true);
    try {
      const path = isMens ? "mensalidades" : "salarios";
      await api.patch(`/financeiro/${path}/${item.id}`, {
        valor: parseFloat(form.valor),
        vencimento: form.vencimento,
        status: form.status,
        observacoes: form.observacoes || null,
      });
      toast.success(`${isMens ? "Mensalidade" : "Salário"} atualizado`);
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao salvar");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="fin-edit-dialog">
        <DialogHeader>
          <DialogTitle>Editar {label}</DialogTitle>
          <DialogDescription>{nome} — competência {item.competencia}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <F label="Valor (R$)" req>
            <Input type="number" step="0.01" value={form.valor} onChange={(e) => setForm({ ...form, valor: e.target.value })} data-testid="fin-edit-valor" />
          </F>
          <F label="Vencimento" req>
            <Input type="date" value={form.vencimento} onChange={(e) => setForm({ ...form, vencimento: e.target.value })} data-testid="fin-edit-venc" />
          </F>
          <F label="Status">
            <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
              <SelectTrigger data-testid="fin-edit-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="pendente">Pendente</SelectItem>
                <SelectItem value="pago">Pago</SelectItem>
                <SelectItem value="atrasado">Atrasado</SelectItem>
                <SelectItem value="cancelado">Cancelado</SelectItem>
              </SelectContent>
            </Select>
          </F>
          <F label="Observações">
            <Textarea rows={2} value={form.observacoes} onChange={(e) => setForm({ ...form, observacoes: e.target.value })} placeholder="Motivo do ajuste, desconto especial, etc." data-testid="fin-edit-obs" />
          </F>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={submit} disabled={saving} className="bg-brand-turquoise hover:bg-[#3A9CA2] text-white" data-testid="fin-edit-submit">
            {saving ? "Salvando…" : "Salvar alterações"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
